import { useEffect, type JSX } from 'react';
import { defaultTransform } from '../../../core/geometry';
import type { BoundsType, Id, ItemTransform } from '../../../core/model';
import { AlignmentPicker, alignmentName } from '../../components/AlignmentPicker';
import { Dialog } from '../../components/Dialog';
import { NumberField } from '../../components/NumberField';
import { useProject, useRuntime, useStudio } from '../../context';
import { useEditSession } from '../../hooks/useEditSession';
import { attempt } from '../../safe';
import { displayedSize, scaleForSize } from './sizeMath';
import '../dialogs.css';

const BOUNDS_LABELS: Record<BoundsType, string> = {
  none: 'No bounds',
  stretch: 'Stretch to bounds',
  'scale-inner': 'Scale to inner bounds',
  'scale-outer': 'Scale to outer bounds',
  'scale-to-width': 'Scale to width of bounds',
  'scale-to-height': 'Scale to height of bounds',
  'max-only': 'Maximum size only',
};

export function TransformDialog({ sceneId, itemId, onClose }: { sceneId: Id; itemId: Id; onClose(): void }): JSX.Element | null {
  const studio = useStudio();
  const transform = useProject((p) => studio.queries.findItem(p, sceneId, itemId)?.item.transform);
  const session = useEditSession(studio);
  // Source sizes arrive asynchronously; re-read the content size when they change.
  useRuntime((r) => r.sources);

  const exists = transform !== undefined;
  useEffect(() => {
    if (!exists) onClose();
  }, [exists, onClose]);
  if (!transform) return null;

  let content = { x: 0, y: 0 };
  try {
    content = studio.getItemContentSize(sceneId, itemId);
  } catch {
    content = { x: 0, y: 0 };
  }
  const size = displayedSize(transform, content);
  const bounded = transform.boundsType !== 'none';
  const seal = (): void => session.commit();
  /** Live edit of one transform property; typing in a field is one undo step per visit to the field. */
  const set = <K extends keyof ItemTransform>(key: K, value: ItemTransform[K]): void =>
    session.edit(
      key,
      () => studio.queries.findItem(studio.project.getState().project, sceneId, itemId)?.item.transform[key] ?? value,
      (next, options) => studio.commands.setItemTransform(sceneId, itemId, { [key]: next }, options),
      value,
    );
  const setNow = (patch: Partial<ItemTransform>): void => {
    seal();
    attempt(studio, () => studio.commands.setItemTransform(sceneId, itemId, patch));
    seal();
  };

  const setSize = (axis: 'x' | 'y', value: number): void => {
    const scale = scaleForSize(transform, content, axis, value);
    if (scale !== null) set('scale', { ...transform.scale, [axis]: scale });
  };

  const num = (label: string, value: number, onChange: (v: number) => void, extra: { min?: number; unit?: string; disabled?: boolean } = {}): JSX.Element => (
    <NumberField label={label} value={value} onChange={onChange} onCommit={seal} unit={extra.unit ?? 'px'} min={extra.min} disabled={extra.disabled} precision={2} />
  );

  const close = (): void => {
    seal();
    onClose();
  };

  return (
    <Dialog
      title="Edit transform"
      onClose={close}
      width={560}
      footer={
        <>
          <button type="button" className="ui-btn" style={{ marginRight: 'auto' }} onClick={() => setNow(defaultTransform())}>
            Reset
          </button>
          <button type="button" className="ui-btn primary" onClick={close}>
            Close
          </button>
        </>
      }
    >
      <div className="transform-grid">
        <span className="lbl">Position</span>
        <div className="ui-row">
          {num('Position X', transform.position.x, (x) => set('position', { ...transform.position, x }))}
          {num('Position Y', transform.position.y, (y) => set('position', { ...transform.position, y }))}
        </div>

        <span className="lbl">Rotation</span>
        <div>{num('Rotation', transform.rotation, (rotation) => set('rotation', rotation), { unit: '°' })}</div>

        <span className="lbl">Size</span>
        <div className="ui-row">
          {num('Width', size.x, (v) => setSize('x', v), { min: 0, disabled: bounded || content.x <= 0 })}
          {num('Height', size.y, (v) => setSize('y', v), { min: 0, disabled: bounded || content.y <= 0 })}
        </div>

        <span className="lbl">Positional alignment</span>
        <div className="ui-row">
          <AlignmentPicker label="Positional alignment" value={transform.alignment} onChange={(alignment) => setNow({ alignment })} />
          <span className="ui-dim">{alignmentName(transform.alignment)}</span>
        </div>

        <label htmlFor="transform-bounds-type">Bounding box type</label>
        <select
          id="transform-bounds-type"
          className="ui-select"
          value={transform.boundsType}
          onChange={(e) => {
            const boundsType = e.target.value as BoundsType;
            // Entering bounds mode starts from the box the item currently occupies.
            const patch: Partial<ItemTransform> = { boundsType };
            if (boundsType !== 'none' && !bounded && size.x > 0 && size.y > 0) patch.bounds = { x: size.x, y: size.y };
            setNow(patch);
          }}
        >
          {(Object.keys(BOUNDS_LABELS) as BoundsType[]).map((type) => (
            <option key={type} value={type}>
              {BOUNDS_LABELS[type]}
            </option>
          ))}
        </select>

        <span className="lbl">Alignment in bounding box</span>
        <div className="ui-row" aria-disabled={!bounded}>
          <AlignmentPicker label="Alignment in bounding box" value={transform.boundsAlignment} onChange={(boundsAlignment) => setNow({ boundsAlignment })} />
          <span className="ui-dim">{bounded ? alignmentName(transform.boundsAlignment) : 'Used with a bounding box'}</span>
        </div>

        <span className="lbl">Bounding box size</span>
        <div className="ui-row">
          {num('Bounds width', transform.bounds.x, (x) => set('bounds', { ...transform.bounds, x }), { min: 1, disabled: !bounded })}
          {num('Bounds height', transform.bounds.y, (y) => set('bounds', { ...transform.bounds, y }), { min: 1, disabled: !bounded })}
        </div>

        <span className="lbl">Crop left / right</span>
        <div className="ui-row">
          {num('Crop left', transform.crop.left, (left) => set('crop', { ...transform.crop, left }), { min: 0 })}
          {num('Crop right', transform.crop.right, (right) => set('crop', { ...transform.crop, right }), { min: 0 })}
        </div>

        <span className="lbl">Crop top / bottom</span>
        <div className="ui-row">
          {num('Crop top', transform.crop.top, (top) => set('crop', { ...transform.crop, top }), { min: 0 })}
          {num('Crop bottom', transform.crop.bottom, (bottom) => set('crop', { ...transform.crop, bottom }), { min: 0 })}
        </div>
      </div>
      <p className="ui-dim">
        Source size: {content.x > 0 ? `${Math.round(content.x)} × ${Math.round(content.y)} px` : 'not known yet'}
      </p>
    </Dialog>
  );
}
